/**
 * Shared utility functions for preflight operations
 */

/**
 * Add recovery segments to preflight check errors (matches Backbone addRecoverySegment)
 */
export const addRecoverySegment = (check) => {
    const updatedCheck = { ...check };

    // Set non_error_warning flag (matches Backbone logic)
    // Non-error-warning means success state without errors or warnings
    const hasErrorsOrWarnings = check.state === 'error' || check.error_type === 'warning';
    updatedCheck.non_error_warning = !hasErrorsOrWarnings;
    updatedCheck.error_class = check.error_type === 'warning' ? 'warning' : check.state;

    // Only process errors if they exist
    if (check.errors && check.errors.length > 0) {
        updatedCheck.errors = check.errors.map((error) => {
            const updatedError = { ...error };

            // Add recovery options based on error codes (matching Backbone logic)
            if (check.state === 'error' && error.error_code === 'source_has_existing_migration') {
                updatedError.recovery = 'source_has_existing_migration_recovery';
            } else if (check.state === 'error' && error.error_code === 'source_has_previous_migration_attempt') {
                updatedError.recovery = 'source_has_previous_migration_attempt_recovery';
            }

            return updatedError;
        });
    }

    return updatedCheck;
};

/**
 * Get resource name from preflight component (matches Backbone logic)
 */
export const getResourceName = (component, migrationContext = {}) => {
    // Check for alternate name configuration (like backbone)
    const alternateNameConfig = migrationContext.alternateName?.[component.type];

    if (alternateNameConfig && component.data) {
        if (alternateNameConfig.path) {
            // Extract value from nested path (e.g., "target_config.properties.name")
            const pathValue = getValueFromPath(component.data, alternateNameConfig.path);
            if (pathValue) return pathValue;
        }
        if (alternateNameConfig.name_function && typeof alternateNameConfig.name_function === 'function') {
            // Use custom name function
            const nameFromFunction = alternateNameConfig.name_function(component.data);
            if (nameFromFunction) return nameFromFunction;
        }
    }

    // First, try to get name from the component's sourceData
    if (component.sourceData?.name) {
        return component.sourceData.name;
    }

    // Fallback to component data (from API response)
    if (component.type === 'target_cloud') {
        return component.data?.name || component.sourceData?.name || 'Target Cloud';
    }

    // For migration_profile, try to get name from various sources
    if (component.type === 'migration_profile') {
        const profileName = component.data?.name || component.sourceData?.name || component.name;
        if (profileName) {
            return profileName;
        }
    }

    // For source_config, try to get target config name via alternate path
    if (component.type === 'source_config' && component.data) {
        // Try the alternate name path first
        const targetConfigName =
            getValueFromPath(component.data, 'target_config.properties.name') ||
            getValueFromPath(component?.sourceData, 'target_config.properties.name');
        if (targetConfigName) {
            return targetConfigName;
        }

        // Try other common name fields
        const configName = component.data.name || component.data.hostname || component.data.host;
        if (configName) {
            return configName;
        }
    }

    if (component.type === 'appliance' && component.sourceData?.cloud_properties?.vapp_name) {
        return component.sourceData.cloud_properties.vapp_name;
    }

    // For sources, prefer host (IP address) over hostname or ID
    const fromData = component.data?.host || component.data?.hostname || component.data?.name;
    return (
        fromData || component.sourceData?.host || component.sourceData?.hostname || component.resource_id || 'Unknown'
    );
};

/**
 * Helper function to get value from nested object path
 * Replicates backbone RM.getValFromPath function
 */
const getValueFromPath = (obj, path) => {
    if (!obj || !path) return null;

    return path.split('.').reduce((current, key) => {
        return current?.[key];
    }, obj);
};

/**
 * Get resource type display name (matches Backbone logic)
 */
export const getResourceType = (component, migrationContext = {}) => {
    // Check for alternate type configuration (like backbone)
    const alternateType = migrationContext.alternateType?.[component.type];
    if (alternateType) {
        return alternateType;
    }

    // Default type mappings
    if (component.type === 'source') return 'Source';
    if (component.type === 'source_config') return 'Source Config';
    if (component.type === 'migration_profile') return 'Migration Profile';
    if (component.type === 'target_cloud') return 'Target Cloud';
    if (component.type === 'source_discovery') return 'Source Discovery';
    if (component.type === 'in_situ_modernization') return 'OS Modernization';

    // Capitalize the first letter if no mapping found
    return component.type.charAt(0).toUpperCase() + component.type.slice(1).replace(/_/g, ' ');
};

/**
 * Get status icon component
 */
export const getStatusIcon = (status, errorType) => {
    if (status === 'success' && !errorType) {
        return { type: 'CheckCircleFilled', style: { color: '#52c41a', fontSize: '16px' } };
    } else if (status === 'success' && errorType === 'warning') {
        return { type: 'ExclamationCircleFilled', style: { color: '#faad14', fontSize: '16px' } };
    } else if (status === 'error') {
        if (errorType === 'warning') {
            return { type: 'ExclamationCircleFilled', style: { color: '#faad14', fontSize: '16px' } };
        }
        return { type: 'CloseCircleFilled', style: { color: '#ff4d4f', fontSize: '16px' } };
    } else if (status === 'running') {
        return { type: 'SyncOutlined', style: { color: '#1890ff', fontSize: '16px' }, spin: true };
    }
    return { type: 'SyncOutlined', style: { color: '#d9d9d9', fontSize: '16px' } };
};

/**
 * Helper functions for status display
 */
export const getStatusColor = (status, errorType) => {
    if (status === 'success' && !errorType) return '#52c41a';
    if (errorType === 'warning') return '#faad14';
    if (status === 'error') return '#ff4d4f';
    return '#d9d9d9';
};

export const getStatusText = (status, errorType) => {
    if (status === 'success' && !errorType) return 'Ready';
    if (status === 'success' && errorType === 'warning') return 'Ready';
    if (status === 'error' && errorType === 'warning') return 'Warning';
    if (status === 'error') return 'Failed';
    if (status === 'running') return 'Running';
    return 'Pending';
};

/**
 * Filter preflight checks based on mode and view all state (matches Backbone logic)
 */
export const getFilteredChecks = (checks, componentId, nonErrorsHiddenPerComponent, inspectSourcesMode) => {
    if (!checks) return [];

    if (inspectSourcesMode) {
        // In source inspection mode, only show errors and warnings
        return checks.filter(
            (check) => check.state === 'error' || (check.state === 'success' && check.error_type === 'warning'),
        );
    }

    // In migration readiness mode, handle view all checks logic
    const nonErrorsHidden = nonErrorsHiddenPerComponent.get(componentId) === true; // Default to false (show all)

    if (nonErrorsHidden) {
        // Show only errors and warnings (hide non-error-warning checks)
        return checks.filter((check) => check.state === 'error' || check.error_type === 'warning');
    } else {
        // Show all checks
        return checks;
    }
};

/**
 * Get source hostname and host information for warning messages
 */
export const getSourceWarningInfo = (component) => {
    const hostname =
        component.data?.hostname ||
        component.sourceData?.hostname ||
        component.data?.name ||
        component.sourceData?.name;
    const host = component.data?.host || component.sourceData?.host || component.resource_id;

    return {
        source_hostname: hostname || host,
        source_host: host,
    };
};

/**
 * Group preflight components by type (similar to Backbone implementation)
 */
export const groupComponentsByType = (preflightComponents) => {
    const groups = {};
    preflightComponents.forEach((component) => {
        const type = component.type || 'unknown';
        if (!groups[type]) {
            groups[type] = [];
        }
        groups[type].push(component);
    });

    // Sort to match Backbone order: target_cloud first, then sources
    const sortedGroups = {};
    if (groups.target_cloud) {
        sortedGroups.target_cloud = groups.target_cloud;
    }
    Object.keys(groups)
        .sort()
        .forEach((key) => {
            if (key !== 'target_cloud') {
                sortedGroups[key] = groups[key];
            }
        });

    return sortedGroups;
};
